'use client'

// Listing image viewer. The primary image renders at its natural aspect ratio
// (capped at 70vh) instead of a cramped 4:3 crop, so poster-style flyers stay
// readable; clicking any image opens a full-screen lightbox (Esc / arrows /
// click-outside to dismiss). Thumbnails jump straight into the lightbox.

import { useState, useEffect, useCallback } from 'react'

export function ImageGallery({
  images,
  title,
  isPromoted,
}: {
  images: string[]
  title: string
  isPromoted: boolean
}) {
  const [lightbox, setLightbox] = useState<number | null>(null)
  const has = images.length > 0

  const close = useCallback(() => setLightbox(null), [])
  const step = useCallback(
    (delta: number) =>
      setLightbox((v) => (v === null ? v : (v + delta + images.length) % images.length)),
    [images.length],
  )

  useEffect(() => {
    if (lightbox === null) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
      else if (e.key === 'ArrowRight') step(1)
      else if (e.key === 'ArrowLeft') step(-1)
    }
    window.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = prevOverflow
    }
  }, [lightbox, close, step])

  return (
    <div className="bg-navy-2 border border-[rgba(255,255,255,0.07)] rounded overflow-hidden">
      <div
        className="relative bg-navy-3 flex items-center justify-center"
        style={{ minHeight: '20rem' }}
      >
        {has ? (
          <button
            type="button"
            onClick={() => setLightbox(0)}
            aria-label="Enlarge image"
            className="w-full flex items-center justify-center cursor-zoom-in"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={images[0]}
              alt={title}
              className="max-w-full max-h-[70vh] object-contain"
            />
          </button>
        ) : (
          <div className="w-full h-80 flex items-center justify-center text-text-3 text-5xl">📦</div>
        )}
        {has && (
          <span className="absolute bottom-3 right-3 font-mono text-[10px] tracking-widest bg-navy/70 text-text-2 px-2 py-1 rounded pointer-events-none">
            🔍 Click to enlarge
          </span>
        )}
        {isPromoted && (
          <span className="absolute top-3 left-3 font-mono text-[10px] tracking-widest bg-amber/90 text-navy px-2 py-1 rounded">
            PROMOTED
          </span>
        )}
      </div>

      {images.length > 1 && (
        <div className="grid grid-cols-5 gap-1 p-1">
          {images.slice(0, 10).map((src, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setLightbox(i)}
              aria-label={`View image ${i + 1}`}
              className="aspect-square bg-navy-3 overflow-hidden rounded cursor-pointer"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={src} alt="" loading="lazy" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      )}

      {lightbox !== null && has && (
        <div
          onClick={close}
          role="dialog"
          aria-modal="true"
          aria-label={title}
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={images[lightbox]}
            alt={title}
            onClick={(e) => e.stopPropagation()}
            className="max-w-full max-h-full object-contain"
          />
          <button
            type="button"
            onClick={close}
            aria-label="Close"
            className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl leading-none"
          >
            ×
          </button>
          {images.length > 1 && (
            <>
              <button
                type="button"
                aria-label="Previous image"
                onClick={(e) => {
                  e.stopPropagation()
                  step(-1)
                }}
                className="absolute left-2 sm:left-4 text-white/80 hover:text-white text-4xl leading-none px-2"
              >
                ‹
              </button>
              <button
                type="button"
                aria-label="Next image"
                onClick={(e) => {
                  e.stopPropagation()
                  step(1)
                }}
                className="absolute right-2 sm:right-4 text-white/80 hover:text-white text-4xl leading-none px-2"
              >
                ›
              </button>
              <div className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono text-xs text-white/70">
                {lightbox + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  )
}
